import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@shared/api';
import { useUserProfileContext, userProfileKeys } from '@entities/user-profile';

import { personalFlowMutationResponseSchema, type PersonalFlowMutationResponse } from './schemas';
import {
  certificationRegistrationProgressQuery,
  personalFlowFeedQuery,
  personalFlowCertificationRegistrationQuery,
  personalFlowGeneralQuery,
  personalFlowKeys,
  personalFlowProgramsQuery,
  personalFlowTrainingProgramQuery,
  trainingProgramProgressQuery,
} from './queries';
import type { PersonalFlowSectionKey } from '../lib/section-budget';

export const PERSONAL_FLOW_WARMING_REFRESH_INTERVAL_MS = 3_000;

function sectionIdentityPart(value: unknown): unknown {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return value;
  const identity = { ...(value as Record<string, unknown>) };
  delete identity.slidesCount;
  return identity;
}

export function hasSamePersonalFlowSectionIdentity(
  previousKey: readonly unknown[] | undefined,
  currentKey: readonly unknown[]
): boolean {
  if (previousKey === undefined || previousKey.length !== currentKey.length) return false;
  return previousKey.every(
    (part, index) =>
      JSON.stringify(sectionIdentityPart(part)) ===
      JSON.stringify(sectionIdentityPart(currentKey[index]))
  );
}

export function resolvePersonalFlowFeedRefreshInterval(input: {
  isWarming: boolean;
  dataUpdateCount: number;
}): number | false {
  return input.isWarming && input.dataUpdateCount < 4
    ? PERSONAL_FLOW_WARMING_REFRESH_INTERVAL_MS
    : false;
}

export function usePersonalFlowFeed(accountId: string) {
  return useQuery({
    ...personalFlowFeedQuery(accountId),
    refetchInterval: (query) => {
      const data = query.state.data;
      const isWarming =
        data?.meta?.cache?.status === 'warming' && data.meta.cache.isComplete === false;
      return resolvePersonalFlowFeedRefreshInterval({
        isWarming,
        dataUpdateCount: query.state.dataUpdateCount,
      });
    },
    // Rebuild the projection when the learner returns from an activity, without polling while
    // they are reading their flow.
    refetchOnMount: 'always',
  });
}

export function usePersonalFlowSections(input: {
  accountId: string;
  profileRef: string;
  registrationRef: string;
  programRef: string;
  slidesCounts?: Partial<Record<PersonalFlowSectionKey, number>>;
  enabled?: boolean;
  progressEnabled?: boolean;
}) {
  const progressEnabled = (input.enabled ?? true) && (input.progressEnabled ?? true);
  const certificationRegistrationOptions = personalFlowCertificationRegistrationQuery({
    accountId: input.accountId,
    profileRef: input.profileRef,
    registrationRef: input.registrationRef,
    slidesCount: input.slidesCounts?.['certification-registration'],
    enabled: input.enabled,
  });
  const certificationRegistration = useQuery({
    ...certificationRegistrationOptions,
    placeholderData: (previousData, previousQuery) =>
      hasSamePersonalFlowSectionIdentity(
        previousQuery?.queryKey,
        certificationRegistrationOptions.queryKey
      )
        ? keepPreviousData(previousData)
        : undefined,
  });
  const certificationRegistrationProgress = useQuery(
    certificationRegistrationProgressQuery({
      accountId: input.accountId,
      profileRef: input.profileRef,
      registrationRef: input.registrationRef,
      enabled: progressEnabled,
    })
  );
  const trainingProgramOptions = personalFlowTrainingProgramQuery({
    accountId: input.accountId,
    profileRef: input.profileRef,
    programRef: input.programRef,
    slidesCount: input.slidesCounts?.['training-program'],
    enabled: input.enabled,
  });
  const trainingProgram = useQuery({
    ...trainingProgramOptions,
    placeholderData: (previousData, previousQuery) =>
      hasSamePersonalFlowSectionIdentity(previousQuery?.queryKey, trainingProgramOptions.queryKey)
        ? keepPreviousData(previousData)
        : undefined,
  });
  const trainingProgramProgress = useQuery(
    trainingProgramProgressQuery({
      accountId: input.accountId,
      profileRef: input.profileRef,
      programRef: input.programRef,
      enabled: progressEnabled,
    })
  );
  const generalOptions = personalFlowGeneralQuery({
    accountId: input.accountId,
    profileRef: input.profileRef,
    slidesCount: input.slidesCounts?.general,
    enabled: input.enabled,
  });
  const general = useQuery({
    ...generalOptions,
    placeholderData: (previousData, previousQuery) =>
      hasSamePersonalFlowSectionIdentity(previousQuery?.queryKey, generalOptions.queryKey)
        ? keepPreviousData(previousData)
        : undefined,
  });

  return {
    certificationRegistration,
    certificationRegistrationProgress,
    trainingProgram,
    trainingProgramProgress,
    general,
  };
}

export function usePersonalFlowSettings(accountId: string, profileRef: string, enabled = true) {
  const context = useUserProfileContext(accountId, 'me', enabled);
  const programs = useQuery(personalFlowProgramsQuery(accountId, profileRef, enabled));
  return { context, programs };
}

export function useSavePersonalFlowSettings(accountId: string, profileRef: string) {
  const queryClient = useQueryClient();

  return useMutation<
    PersonalFlowMutationResponse[],
    Error,
    { currentRegistrationId?: string; mainProgramId?: string }
  >({
    mutationFn: async ({ currentRegistrationId, mainProgramId }) => {
      const results: PersonalFlowMutationResponse[] = [];
      if (currentRegistrationId !== undefined) {
        results.push(
          await api.put(
            `/api/v1/user-profiles/${encodeURIComponent(profileRef)}/current-registration`,
            { registration_id: currentRegistrationId },
            { schema: personalFlowMutationResponseSchema }
          )
        );
      }
      if (mainProgramId !== undefined) {
        results.push(
          await api.put(
            `/api/v1/user-profiles/${encodeURIComponent(profileRef)}/main-program`,
            { program_id: mainProgramId },
            { schema: personalFlowMutationResponseSchema }
          )
        );
      }
      return results;
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: personalFlowKeys.actor(accountId) }),
        queryClient.cancelQueries({ queryKey: userProfileKeys.actor(accountId) }),
      ]);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: personalFlowKeys.actor(accountId) }),
        queryClient.invalidateQueries({ queryKey: userProfileKeys.actor(accountId) }),
      ]);
    },
  });
}
